import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Product {
  id: string
  sku: string
  name: string
  description: string
  category: string
  price: number
  currency: string
  status: string
  stock_quantity: number
  markets: string[]
  images: string[]
  attributes: Record<string, any>
  created_at: string
  updated_at: string
}

interface Category {
  id: string
  name: string
  product_count: number
}

export default function ProductCatalogPage() {
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [statistics, setStatistics] = useState<{total_products: number; active: number; categories: number; avg_price: number} | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)

  useEffect(() => {
    fetchData()
  }, [selectedCategory])

  const fetchData = async () => {
    const catParam = selectedCategory !== 'all' ? `?category=${selectedCategory}` : ''
    const [prodRes, catRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/products' + catParam),
      fetch(API_BASE + '/products/categories'),
      fetch(API_BASE + '/products/statistics')
    ])
    if (prodRes.ok) setProducts((await prodRes.json()).products || [])
    if (catRes.ok) setCategories((await catRes.json()).categories || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'inactive': return 'bg-gray-100 text-gray-800'
      case 'out_of_stock': return 'bg-red-100 text-red-800'
      case 'discontinued': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Product Catalog</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            + Add Product
          </button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Total Products</div>
              <div className="text-3xl font-bold">{statistics.total_products}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-3xl font-bold text-green-600">{statistics.active}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Categories</div>
              <div className="text-3xl font-bold">{statistics.categories}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Avg Price</div>
              <div className="text-3xl font-bold">${statistics.avg_price.toFixed(2)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-6">
          {/* Categories Sidebar */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="font-semibold mb-4">Categories</h2>
            <div className="space-y-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`w-full text-left px-3 py-2 rounded-lg ${
                  selectedCategory === 'all' ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'
                }`}
              >
                All Products
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg flex justify-between items-center ${
                    selectedCategory === cat.id ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'
                  }`}
                >
                  <span>{cat.name}</span>
                  <span className="text-xs bg-gray-200 px-2 py-1 rounded">{cat.product_count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div className="col-span-2">
            <div className="grid grid-cols-2 gap-4">
              {products.map(product => (
                <div
                  key={product.id}
                  className={`bg-white rounded-lg shadow overflow-hidden cursor-pointer hover:shadow-lg transition-shadow ${
                    selectedProduct?.id === product.id ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className="h-32 bg-gray-200 flex items-center justify-center">
                    {product.images.length > 0 ? (
                      <img src={product.images[0]} alt={product.name} className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-4xl">📦</span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium">{product.name}</div>
                        <div className="text-xs text-gray-500">{product.sku}</div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(product.status)}`}>
                        {product.status}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-lg font-bold">{formatCurrency(product.price, product.currency)}</div>
                      <div className="text-sm text-gray-500">{product.stock_quantity} in stock</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Product Details */}
          <div>
            {selectedProduct ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedProduct.name}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-500">SKU</div>
                    <div className="font-mono">{selectedProduct.sku}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Price</div>
                    <div className="text-2xl font-bold">
                      {formatCurrency(selectedProduct.price, selectedProduct.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Stock</div>
                    <div className={`font-medium ${
                      selectedProduct.stock_quantity > 10 ? 'text-green-600' :
                      selectedProduct.stock_quantity > 0 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {selectedProduct.stock_quantity} units
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Category</div>
                    <div>{selectedProduct.category}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Markets</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedProduct.markets.map(m => (
                        <span key={m} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{m}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Description</div>
                    <div className="text-sm">{selectedProduct.description}</div>
                  </div>
                  <div className="pt-4 space-y-2">
                    <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      Edit Product
                    </button>
                    <button className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50">
                      View Analytics
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="font-medium text-green-800">Top Seller</div>
                    <div className="text-sm text-green-600 mt-1">
                      Widget Pro - 1,234 units sold
                    </div>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="font-medium text-red-800">Low Stock Alert</div>
                    <div className="text-sm text-red-600 mt-1">
                      3 products need restocking
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
